'use client';

import { useAuth } from '@/contexts/AuthProvider';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceSocket } from '@/hooks/useWorkspaceSocket';
import { IWorkflowNotification } from '@/types/plays';
import { createContext, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { io } from 'socket.io-client';

// Initialize Socket.IO instance (shared globally)
export const socketIo = io(process.env.NEXT_PUBLIC_SOCKET_URL!, {
  path: process.env.NEXT_PUBLIC_SOCKET_IO_HUB_PATH,
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 10000,
  randomizationFactor: 0.5,
});

interface NotificationContextType {
  notifications: IWorkflowNotification[];
  connectedCount: number;
  isConnected: boolean;
  resetCount: () => void;
  subscribeToAudience: (audienceId: string | number) => void;
  unsubscribeFromAudience: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const { user } = useAuth();
  const workspaceContext = useWorkspace();
  const activeWorkspace = workspaceContext?.activeWorkspace;

  const [isConnected, setIsConnected] = useState(socketIo.connected);
  const [notifications, setNotifications] = useState<IWorkflowNotification[]>([]);
  const [connectedCount, setConnectedCount] = useState(0);
  const notificationKeysRef = useRef<Set<string>>(new Set());
  const currentAudienceRef = useRef<string | number | null>(null);

  // Use workspace-aware socket management
  useWorkspaceSocket(socketIo);

  // Generate secure, workspace-scoped session ID
  const userSession = useMemo(() => {
    if (!activeWorkspace || !user) return null;
    return `workspace_${activeWorkspace.vendorid}_user_${user.id}_${Date.now()}`;
  }, [activeWorkspace?.vendorid, user?.id]);

  // Socket.IO event handlers
  useEffect(() => {
    const handleRunUpdate = (data: IWorkflowNotification) => {
      console.log('🔔 Global notification received:', data);
      const uniqueKey = data.timestamp;
      if (!uniqueKey) return;

      const makeKey = (n: IWorkflowNotification) => `${n.worker}|${n.state}|${n.timestamp}`;
      const incomingKey = makeKey(data);

      const isDupe = notificationKeysRef.current.has(incomingKey);

      if (!isDupe) {
        notificationKeysRef.current.add(incomingKey);
        setConnectedCount((count) => count + 1);
        setNotifications((prev) => [...prev, data]);
        console.log('🔔 NEW GLOBAL NOTIFICATION! Count now:', connectedCount + 1);
      }
    };

    const handleConnect = () => {
      console.log('✅ Global Socket.IO connected to Azure Web PubSub');
      console.log('📡 Socket ID:', socketIo.id);
      setIsConnected(true);

      // Re-subscribe to current audience if there is one
      if (currentAudienceRef.current && userSession && activeWorkspace && user) {
        console.log('🔌 Re-subscribing to audience after reconnect:', currentAudienceRef.current);
        socketIo.emit('subscribe_audience', {
          audienceId: currentAudienceRef.current,
          userSession,
          workspaceId: activeWorkspace.vendorid,
          userId: user.id,
        });
      }
    };

    const handleDisconnect = () => {
      console.log('❌ Global Socket.IO disconnected from Azure Web PubSub');
      setIsConnected(false);
    };

    // Add event listeners
    socketIo.on('connect', handleConnect);
    socketIo.on('disconnect', handleDisconnect);
    socketIo.on('run_update', handleRunUpdate);

    // If already connected, update state
    if (socketIo.connected) {
      setIsConnected(true);
    }

    return () => {
      socketIo.off('connect', handleConnect);
      socketIo.off('disconnect', handleDisconnect);
      socketIo.off('run_update', handleRunUpdate);
    };
  }, [userSession, activeWorkspace?.vendorid, user?.id, connectedCount]);

  const subscribeToAudience = (audienceId: string | number) => {
    if (!userSession || !activeWorkspace || !user) {
      console.log('❌ Cannot subscribe - missing auth context');
      return;
    }

    console.log('🔌 Subscribing to audience:', audienceId);
    currentAudienceRef.current = audienceId;

    if (socketIo.connected) {
      socketIo.emit('subscribe_audience', {
        audienceId,
        userSession,
        workspaceId: activeWorkspace.vendorid,
        userId: user.id,
      });
    }
  };

  const unsubscribeFromAudience = () => {
    console.log('🔌 Unsubscribing from current audience');
    currentAudienceRef.current = null;
  };

  const resetCount = () => {
    setConnectedCount(0);
  };

  const contextValue: NotificationContextType = {
    notifications,
    connectedCount,
    isConnected,
    resetCount,
    subscribeToAudience,
    unsubscribeFromAudience,
  };

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  );
};
